import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Loader2, KeyRound, Trash2 } from "lucide-react";
import { AdminTitle, OwnerOnly } from "./AdminLayout";
import { Confirm } from "./Confirm";
import { http, apiError, formatDate } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const ROLE_ACCESS = {
  owner: "Full access: inventory, inquiries, sourcing, reviews, company settings, website content and team access.",
  manager: "Operations: inventory listings (no deleting), customer inquiries, sourcing requests and follow-ups. No access to reviews, company settings or team access.",
};

const NewUser = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", title: "Manager", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/admin/users", f);
      toast.success("Account created");
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border hairline p-6 bg-[var(--ink-2)] grid sm:grid-cols-2 gap-4" data-testid="new-user-form">
      <p className="eyebrow sm:col-span-2">New staff account (manager access)</p>
      <input required className="field" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" />
      <input required type="email" className="field" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" />
      <input className="field" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-user-title" />
      <input required minLength={8} type="password" className="field" placeholder="Temporary password (min 8)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" />
      <button className="btn-gold sm:col-span-2 justify-self-start" disabled={busy} data-testid="new-user-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Create account</button>
    </form>
  );
};

function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await http.get("/admin/users")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-users">
      <AdminTitle title="Team & access" sub="Each person has their own login. Passwords are never shared or shown.">
        <button className="btn-gold" onClick={() => setAdding(!adding)} data-testid="add-user-button"><Plus className="w-4 h-4" /> Add staff account</button>
      </AdminTitle>
      {adding && <div className="mb-6"><NewUser onDone={() => { setAdding(false); refresh(); }} /></div>}
      {isLoading ? <p className="text-slate-400">Loading…</p> : (
        <div className="space-y-3">
          {(data?.items || []).map((u) => (
            <div key={u.id} className="border hairline bg-[var(--ink-2)] p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`user-row-${u.role}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-mono text-[0.65rem] uppercase px-2 py-1 border ${u.role === "owner" ? "border-[var(--gold)] text-gold" : "border-sky-400/40 text-sky-300"}`}>{u.role}</span>
                  {!u.active && <span className="font-mono text-[0.65rem] uppercase px-2 py-1 border border-rose-400/40 text-rose-300">Deactivated</span>}
                  {u.id === me?.id && <span className="text-xs text-slate-400">(you)</span>}
                </div>
                <p className="font-display text-2xl mt-2">{u.name} <span className="text-base text-slate-400">· {u.title}</span></p>
                <p className="text-sm text-slate-300 break-all">{u.email}</p>
                <p className="text-xs text-slate-400 mt-2 max-w-2xl">{ROLE_ACCESS[u.role]}</p>
                {u.created_at && <p className="text-xs text-slate-500 mt-1">Added {formatDate(u.created_at)}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => act(() => http.post(`/admin/users/${u.id}/send-reset`), `Reset link sent to ${u.email}`)} data-testid={`user-send-reset-${u.role}`}><KeyRound className="w-3 h-3" /> Send reset link</button>
                {u.role !== "owner" && (
                  <>
                    <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => act(() => http.patch(`/admin/users/${u.id}`, { active: !u.active }), u.active ? "Account deactivated" : "Account reactivated")} data-testid={`user-toggle-active-${u.id}`}>{u.active ? "Deactivate" : "Reactivate"}</button>
                    <Confirm title="Remove this account?" text={`${u.name} will no longer be able to sign in.`} onConfirm={() => act(() => http.delete(`/admin/users/${u.id}`), "Account removed")}>
                      <button className="btn-ghost !py-2 !px-3 !text-xs hover:!border-rose-400" data-testid={`user-delete-${u.id}`}><Trash2 className="w-3 h-3" /></button>
                    </Confirm>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UsersAdmin() {
  return <OwnerOnly><Users /></OwnerOnly>;
}
